import { createTheme, Theme } from "@mui/material/styles";
import { OrganizationBrandingState } from "@/contexts/OrganizationBrandContext";
import {
  breakpoints,
  fonts,
  getTokens,
  modules,
  radii,
  statusColors,
  PdsMode,
} from "./tokens";

const colorPresets = {
  pds: { accent: "#00B4D8", accentLight: "#0D7A97" },
  ocean: { accent: "#5AA9FF", accentLight: "#2563A8" },
  violet: { accent: "#A78BFA", accentLight: "#6D4DB3" },
  emerald: { accent: "#34D399", accentLight: "#087A55" },
  amber: { accent: "#FBBF24", accentLight: "#946200" },
  coral: { accent: "#FB8A70", accentLight: "#B93815" },
  lime: { accent: "#A3E635", accentLight: "#4D7C0F" },
  indigo: { accent: "#818CF8", accentLight: "#444CE7" },
} as const;

const typographyPresets = {
  pds: fonts,
} as const;

export function createPdsTheme(
  branding: OrganizationBrandingState,
  mode: PdsMode
): Theme {
  const tokens = getTokens(mode);
  const colors =
    colorPresets[branding.colorPreset as keyof typeof colorPresets] ||
    colorPresets.pds;
  const type =
    typographyPresets[
      branding.typographyPreset as keyof typeof typographyPresets
    ] || typographyPresets.pds;
  const accent = mode === "light" ? colors.accentLight : colors.accent;

  return createTheme({
    breakpoints: {
      values: {
        xs: breakpoints.mobile,
        sm: 600,
        md: breakpoints.tablet,
        lg: breakpoints.laptop,
        xl: breakpoints.wide,
      },
    },
    palette: {
      mode,
      primary: {
        main: accent,
        contrastText: mode === "dark" ? "#06090f" : "#FFFFFF",
      },
      secondary: { main: modules.cultivation },
      success: {
        main: statusColors.success,
        contrastText: mode === "dark" ? "#0B1F3A" : "#FFFFFF",
      },
      warning: {
        main: statusColors.warning,
        contrastText: mode === "dark" ? "#0B1F3A" : "#FFFFFF",
      },
      error: {
        main: statusColors.error,
        contrastText: mode === "dark" ? "#0B1F3A" : "#FFFFFF",
      },
      info: {
        main: statusColors.info,
        contrastText: mode === "dark" ? "#0B1F3A" : "#FFFFFF",
      },
      background: { default: tokens.bg, paper: tokens.panel },
      text: { primary: tokens.text, secondary: tokens.muted },
      divider: tokens.border,
    },
    shape: { borderRadius: radii.control },
    typography: {
      fontFamily: type.body,
      h1: {
        fontFamily: type.display,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        fontSize: "clamp(2rem, 4vw, 3.5rem)",
      },
      h2: {
        fontFamily: type.display,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
      },
      h3: {
        fontFamily: type.display,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
      },
      h4: {
        fontFamily: type.display,
        fontWeight: 600,
        letterSpacing: "0.02em",
        fontSize: "clamp(1.3rem, 2vw, 1.8rem)",
      },
      h5: {
        fontFamily: type.display,
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
      h6: {
        fontFamily: type.display,
        fontWeight: 600,
        letterSpacing: "0.02em",
      },
      body1: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "0.8125rem",
        lineHeight: 1.45,
      },
      subtitle1: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: "0.8125rem",
        lineHeight: 1.5,
        fontWeight: 600,
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.4,
      },
      overline: {
        fontSize: "0.6875rem",
        lineHeight: 1.4,
      },
      button: {
        fontFamily: type.body,
        fontWeight: 600,
        letterSpacing: "0.06em",
        fontSize: "0.75rem",
        lineHeight: 1.5,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { overflowX: "hidden" },
          "code, pre, [data-pds-data]": {
            fontFamily: type.data,
            fontVariantNumeric: "tabular-nums",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none", borderColor: tokens.border },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${tokens.border}`,
            borderRadius: radii.panel,
            boxShadow: "none",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radii.control,
            fontSize: "0.75rem",
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: radii.control } } },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${tokens.border}`,
            borderRadius: radii.panel,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontFamily: type.display, fontWeight: 600 } },
      },
      MuiTableHead: {
        styleOverrides: { root: { backgroundColor: tokens.panel2 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: tokens.label,
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          },
          root: {
            borderColor: tokens.border2,
            fontSize: "0.8rem",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: radii.control } },
      },
      MuiInputBase: {
        styleOverrides: { root: { fontSize: "0.875rem" } },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: "0.875rem" } },
      },
      MuiCheckbox: { defaultProps: { color: "primary" } },
      MuiRadio: { defaultProps: { color: "primary" } },
      MuiSwitch: { defaultProps: { color: "primary" } },
    },
  });
}
